// src/utils/excelImageColumn.ts
import ExcelJS from 'exceljs';

// First element: a single File (the workbook)
// Second element: an array of Files (the images)
type WorkbookAndImages = [File, File[]];

//---------------------------------File component--------------------------------/
/** Case-insensitive header lookup; returns 1-based column index */
function findHeaderColumnIndex(headerRow: ExcelJS.Row, name: string): number {
  const values = headerRow.values as (string | number | null)[];
  for (let i = 1; i < values.length; i++) {
    const v = values[i];
    if (typeof v === 'string' && v.trim().toLowerCase() === name.toLowerCase()) {
      return i;
    }
  }
  return -1;
}

/** A1 -> column index (1-based) */
function a1ColToIndex(colA1: string): number {
  return colA1
    .toUpperCase()
    .split('')
    .reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 64), 0);
}

/** Uint8Array -> Base64 */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const sub = bytes.subarray(i, i + chunk);
    binary += String.fromCharCode.apply(null, Array.from(sub));
  }
  return btoa(binary);
}

const fileNameWithoutExt = (name: string): string => name.replace(/\.[^.]+$/, '');

async function blobToDataUrl(blob: Blob, fallbackName?: string): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const base64 = uint8ToBase64(bytes);
  const mime = blob.type || (fallbackName ? inferMimeFromName(fallbackName) : undefined) || 'application/octet-stream';
  return `data:${mime};base64,${base64}`;
}

const inferMimeFromName = (name: string): string | undefined => {
    const ext = name.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'bmp':
        return 'image/bmp';
      default:
        return undefined;
    }
  };

type CompressOptions = {
  /** hard cap after compression */
  maxBytes: number;
  /** resize cap */
  maxWidth: number;
  maxHeight: number;
  /** preferred output mime (webp if available, else jpeg) */
  preferMime?: 'image/webp' | 'image/jpeg';
  /** quality tuning */
  initialQuality: number;
  minQuality: number;
  qualityStep: number;
  /** if still too big, downscale more */
  downscaleStep: number; // e.g. 0.85
  maxAttempts: number;
};

function isImageFile(f: File): boolean {
  return (f.type?.startsWith('image/') ?? false) || !!inferMimeFromName(f.name);
}

async function decodeToBitmap(file: File): Promise<{ bitmap: ImageBitmap; width: number; height: number }> {
  // Use createImageBitmap when available (fast & clean)
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { bitmap, width: bitmap.width, height: bitmap.height };
  }

  // Fallback for older browsers
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');
    ctx.drawImage(img, 0, 0);

    // Convert to bitmap via canvas
    const bmp = await createImageBitmap(canvas);
    return { bitmap: bmp, width: bmp.width, height: bmp.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Image compression failed (toBlob returned null).'))),
      mime,
      quality
    );
  });
}

/**
 * Compress an image file by resizing + encoding (webp/jpeg).
 * If output still exceeds maxBytes, it keeps reducing quality and/or dimensions.
 */
async function compressImageFile(file: File, opts: CompressOptions): Promise<File> {
  if (!isImageFile(file)) {
    throw new Error(`"${file.name}" is not a supported image file.`);
  }

  const { bitmap, width, height } = await decodeToBitmap(file);

  try{
  // Start with resize-to-fit
  const fitScale = Math.min(1, opts.maxWidth / width, opts.maxHeight / height);
  let scale = fitScale;

  // Choose mime
  // Prefer WEBP when possible; if browser doesn't support, fallback to JPEG automatically.
  let mime: 'image/webp' | 'image/jpeg' = opts.preferMime ?? 'image/webp';

  const makeCanvas = (w: number, h: number) => {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');
    ctx.drawImage(bitmap, 0, 0, c.width, c.height);
    return c;
  };

  const tryEncode = async (targetMime: string, quality: number, w: number, h: number) => {
    const canvas = makeCanvas(w, h);
    return canvasToBlob(canvas, targetMime, quality);
  };

  // Iteratively try:
  //  - encode webp/jpeg with decreasing quality
  //  - if still large, downscale more and retry
  let attempt = 0;
  let bestBlob: Blob | null = null;
  let bestSize = Number.POSITIVE_INFINITY;

  let currentW = Math.round(width * scale);
  let currentH = Math.round(height * scale);

  while (attempt < opts.maxAttempts) {
    attempt++;

    // quality loop
    let q = opts.initialQuality;
    while (q >= opts.minQuality) {
      let blob: Blob | null = null;

      try {
        blob = await tryEncode(mime, q, currentW, currentH);
      } catch {
        // If WEBP not supported, fallback to JPEG and retry once
        if (mime === 'image/webp') {
          mime = 'image/jpeg';
          blob = await tryEncode(mime, q, currentW, currentH);
        } else {
          throw new Error(``);
        }
      }

      if (blob.size < bestSize) {
        bestSize = blob.size;
        bestBlob = blob;
      }

      if (blob.size <= opts.maxBytes) {
        // success
        const outName = fileNameWithoutExt(file.name) + (mime === 'image/webp' ? '.webp' : '.jpg');
        return new File([blob], outName, { type: blob.type || mime });
      }

      q -= opts.qualityStep;
    }

    // If quality floor reached and still too large => downscale and retry
    scale *= opts.downscaleStep;
    currentW = Math.max(1, Math.round(width * scale));
    currentH = Math.max(1, Math.round(height * scale));
  }

  // Fall back to best attempt; enforce size rule by throwing if still > maxBytes
  if (!bestBlob) {
    throw new Error(`Failed to compress "${file.name}".`);
  }

  if (bestBlob.size > opts.maxBytes) {
    const mb = (bestBlob.size / (1024 * 1024)).toFixed(2);
    const cap = (opts.maxBytes / (1024 * 1024)).toFixed(0);
    throw new Error(`"${file.name}" is still ${mb} MB after compression. Image must be <= ${cap} MB.`);
  }

  const outName = fileNameWithoutExt(file.name) + (mime === 'image/webp' ? '.webp' : '.jpg');
  return new File([bestBlob], outName, { type: bestBlob.type || mime });
  } finally {
    if (bitmap.close) {
      bitmap.close();
    }
  }
}

/**
 * Convert pictures anchored in the "picture" column into Base64 in a new "Image" column.
 * Returns a new File with the modifications, no download.
 */
export async function convertExcelPicturesToImageColumn(
  files: WorkbookAndImages,
  options?: {
    sheetName?: string;
    pictureHeader?: string; // default: "picture"
    newHeader?: string;     // default: "Image"
    // optional compression knobs if you want to tune later
    compression?: Partial<CompressOptions>;
  }
): Promise<File> {
  const [workbookFile, imageFiles] = files;
  const { sheetName, pictureHeader = 'picture', newHeader = 'Image', compression } = options || {};

  // Load workbook
  const arrayBuffer = await workbookFile.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  // Resolve worksheet
  const ws = sheetName ? wb.getWorksheet(sheetName) : wb.worksheets[0];
  if (!ws) throw new Error('Worksheet not found');

  // --- Find columns based on header row ---
  const headerRow = ws.getRow(1);
  const pictureCol = findHeaderColumnIndex(headerRow, pictureHeader);
  if (pictureCol === -1) throw new Error(`Header "${pictureHeader}" not found`);

  // Insert "Image" column (right after picture), if not present
  let imageCol = findHeaderColumnIndex(headerRow, newHeader);
  if (imageCol === -1) {
    ws.spliceColumns(pictureCol + 1, 0, []); // insert only, don't delete
    imageCol = pictureCol + 1;
    ws.getCell(1, imageCol).value = newHeader;
  }

  // ✅ NEW: enforce 5MB AFTER compression (instead of 15MB raw)
  const MAX_COMPRESSED_BYTES = 5 * 1024 * 1024; // 5 MB

  const compressOpts: CompressOptions = {
    maxBytes: MAX_COMPRESSED_BYTES,
    maxWidth: 1600,
    maxHeight: 1600,
    preferMime: 'image/webp',
    initialQuality: 0.82,
    minQuality: 0.5,
    qualityStep: 0.07,
    downscaleStep: 0.85,
    maxAttempts: 8,
    ...(compression ?? {}),
  };

  // --- Build ID->base64 map from imageFiles ---
  const imageMap = new Map<string, string>(); // id -> base64 data URL

  for (const imgFile of imageFiles) {
    const id = fileNameWithoutExt(imgFile.name);

    // Compress first
    const compressedFile = await compressImageFile(imgFile, compressOpts);

    //Check size AFTER compression
    if (compressedFile.size > MAX_COMPRESSED_BYTES) {
      const mb = (compressedFile.size / (1024 * 1024)).toFixed(2);
      throw new Error(`"${imgFile.name}" is still ${mb} MB after compression. Image must be <= 5 MB.`);
    }

    const b64 = await blobToDataUrl(compressedFile, compressedFile.name);
    imageMap.set(id, b64);
  }

  // --- Write Base64 into the "Image" column using the ID in "picture" column ---
  ws.eachRow((row, r) => {
    if (r === 1) return; // skip header
    const rawId = row.getCell(pictureCol).value;
    const id =
      typeof rawId === 'string'
        ? rawId.trim()
        : typeof rawId === 'number'
        ? String(rawId)
        : (rawId as any)?.toString?.().trim?.() ?? '';
    if (!id) return;

    const b64 = imageMap.get(fileNameWithoutExt(id));
    if (b64) {
      row.getCell(imageCol).value = b64;
    }
  });

  // --- Produce a new File without downloading ---
  const outBuffer = await wb.xlsx.writeBuffer(); // ArrayBuffer
  const outBlob = new Blob([outBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const baseName = fileNameWithoutExt(workbookFile.name);
  const outName = `${baseName}_with_images.xlsx`;
  return new File([outBlob], outName, { type: outBlob.type });
}


/** Batch version: convert every file and return a File[] */
export async function convertBatchToImageColumn(
  files: WorkbookAndImages,
  options?: { sheetName?: string; pictureHeader?: string; newHeader?: string; compression?: Partial<CompressOptions> }
): Promise<File[]> {
  const results: File[] = [];
//   for (const f of files) {
    const converted = await convertExcelPicturesToImageColumn(files, options);
    results.push(converted);
//   }
  return results;
}